import { useState } from 'react';

import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { useTranslation } from 'react-i18next';

import { Button as UiButton } from '@/components/ui/button';
import {
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Dialog as UiDialog,
} from '@/components/ui/dialog';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useRevisionIncrement } from '@/hooks/useRevisionIncrement';
import { formatURLDate } from '@/utils/formatting';

interface TopRevisionsButtonProps {
  disabled?: boolean;
  date?: string;
  label?: string;
  icon?: string;
  size?: string;
  className?: string;
  tooltip?: string;
  type?: 'button' | 'submit' | 'reset';
  hideTrigger?: boolean;
  visible?: boolean;
  onHide?: () => void;
}

export const TopRevisionsButton = ({
  label,
  icon,
  size,
  type,
  className,
  tooltip,
  disabled = false,
  date,
  hideTrigger = false,
  visible,
  onHide,
}: TopRevisionsButtonProps) => {
  const { t } = useTranslation();
  const isBaseUi = useFeatureFlag('top_revisions_prime_to_base_ui');
  const [internalVisible, setInternalVisible] = useState(false);

  const { allTopRevisionNumbers } = useRevisionIncrement(date);

  const dialogVisible = visible !== undefined ? visible : internalVisible;
  const handleHide = onHide || (() => setInternalVisible(false));

  if (isBaseUi) {
    return (
      <>
        {!hideTrigger && (
          <div className="label-input-group">
            <label> </label>
            <UiButton
              size={
                size === 'small' ? 'sm' : size === 'large' ? 'lg' : 'default'
              }
              className={className}
              title={tooltip}
              type={type}
              onClick={() => {
                if (visible === undefined) {
                  setInternalVisible(true);
                }
              }}
              disabled={disabled}
            >
              {icon && <i className={icon} />}
              {label}
            </UiButton>
          </div>
        )}
        <UiDialog
          open={dialogVisible}
          onOpenChange={(open) => {
            if (!open) handleHide();
          }}
        >
          <DialogContent
            preflight={false}
            className="z-[2000] md:max-w-[80vw]"
            overlayClassName="z-[2000]"
          >
            <DialogHeader className="preflight">
              <DialogTitle>
                {t('revision.topRevisionsDialogTitle', {
                  date: formatURLDate(date ?? ''),
                })}
              </DialogTitle>
            </DialogHeader>
            <DialogBody>
              <DataTable
                value={allTopRevisionNumbers ?? []}
                scrollable
                scrollHeight="400px"
                className="w-full"
              >
                <Column
                  field="revisionNumber"
                  header={t('revision.revisionNumber')}
                />
                <Column
                  field="fertilizationSiteName"
                  header={t('revision.fertilizationSiteName')}
                />
              </DataTable>
            </DialogBody>
          </DialogContent>
        </UiDialog>
      </>
    );
  }

  return (
    <>
      {!hideTrigger && (
        <div className="label-input-group">
          <label> </label>
          <Button
            label={label}
            icon={icon}
            size={size as 'small' | 'large'}
            className={className}
            tooltip={tooltip}
            tooltipOptions={{ position: 'left' }}
            type={type}
            onClick={() => {
              if (visible === undefined) {
                setInternalVisible(true);
              }
            }}
            disabled={disabled}
          />
        </div>
      )}
      <Dialog
        visible={dialogVisible}
        onHide={handleHide}
        header={`All Top Revision Numbers Per Fertilization Site ${formatURLDate(date ?? '')}`}
        className="w-[80vw]"
      >
        <DataTable
          value={allTopRevisionNumbers ?? []}
          scrollable
          scrollHeight="400px"
          className="w-full"
        >
          <Column field="revisionNumber" header="Revision Number" />
          <Column
            field="fertilizationSiteName"
            header="Fertilization Site Name"
          />
        </DataTable>
      </Dialog>
    </>
  );
};
